import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { formatDateTimeShort, formatIsoDateShort } from "@/lib/bookingDates";
import { formatCurrency } from "@/lib/money";
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  paymentMethodLabel,
  type PaymentMethod,
} from "@/lib/paymentMethod";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { Table } from "@/components/ui/Table";
import type { PaymentJournalListResponse } from "@/types/camping";

const PAGE_SIZE = 100;

function todayLocalDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function csvEscape(value: string): string {
  if (value.includes('"') || value.includes(";") || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function downloadCsv(filename: string, rows: string[][]) {
  const bom = "\uFEFF";
  const body = rows.map((row) => row.map(csvEscape).join(";")).join("\n");
  const blob = new Blob([bom + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function CashbookPage() {
  const { setPageMeta } = usePageHeader();

  const [fromDate, setFromDate] = useState(todayLocalDate());
  const [toDate, setToDate] = useState(todayLocalDate());
  const [paymentMethod, setPaymentMethod] = useState<"" | PaymentMethod>("");
  const [offset, setOffset] = useState(0);

  const [data, setData] = useState<PaymentJournalListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String(offset),
    });
    if (fromDate) params.set("from_date", fromDate);
    if (toDate) params.set("to_date", toDate);
    if (paymentMethod) params.set("payment_method", paymentMethod);

    api
      .get<PaymentJournalListResponse>(`/api/v1/payments?${params}`)
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [fromDate, toDate, paymentMethod, offset]);

  useEffect(() => {
    setPageMeta({
      title: "Kassenjournal",
      subtitle: "Zahlungseingänge nach Tag und Zeitraum",
    });
    return () => setPageMeta(null);
  }, [setPageMeta]);

  useEffect(() => {
    load();
  }, [load]);

  const currency = data?.currency ?? "EUR";
  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const totalsByMethod = data?.totals_by_method ?? [];

  const methodSummaryRows = useMemo(
    () =>
      [...totalsByMethod].sort((a, b) =>
        paymentMethodLabel(a.payment_method).localeCompare(
          paymentMethodLabel(b.payment_method),
          "de",
        ),
      ),
    [totalsByMethod],
  );

  function onFilterSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (offset !== 0) {
      setOffset(0);
      return;
    }
    load();
  }

  function setToday() {
    const t = todayLocalDate();
    setFromDate(t);
    setToDate(t);
    setOffset(0);
  }

  function exportCsv() {
    if (!data || items.length === 0) return;
    const header = [
      "Datum/Zeit",
      "Methode",
      "Betrag",
      "Rechnungsnr.",
      "Gast",
      "Buchungsnr.",
      "Referenz",
      "Notiz",
    ];
    const rows = items.map((p) => [
      formatDateTimeShort(p.paid_at),
      paymentMethodLabel(p.payment_method),
      p.amount,
      p.invoice_number,
      p.guest_name,
      p.booking_number ?? "",
      p.reference ?? "",
      p.notes ?? "",
    ]);
    const period = fromDate === toDate ? fromDate : `${fromDate}_${toDate}`;
    downloadCsv(`kassenjournal_${period}.csv`, [header, ...rows]);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-label-secondary">
          Lesen aus verbuchten Zahlungen – keine separate Kassenbuchführung.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/daily-closing"
            className="text-sm font-medium text-accent hover:underline"
          >
            Tagesabschluss →
          </Link>
          <Link
            to="/invoices"
            className="text-sm font-medium text-accent hover:underline"
          >
            ← Rechnungen
          </Link>
        </div>
      </div>

      <div className="rounded-xl border border-separator bg-surface-secondary/40 px-4 py-3 sm:flex sm:items-center sm:justify-between sm:gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-label-secondary">
            Summe im Filter
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
            {formatCurrency(data?.grand_total ?? "0", currency)}
          </p>
        </div>
        <p className="mt-2 text-sm text-label-secondary sm:mt-0">
          {total} Zahlung{total === 1 ? "" : "en"}
          {fromDate && toDate ? (
            <>
              {" "}
              · {fromDate === toDate
                ? formatIsoDateShort(fromDate)
                : `${formatIsoDateShort(fromDate)} – ${formatIsoDateShort(toDate)}`}
            </>
          ) : null}
        </p>
      </div>

      {methodSummaryRows.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {methodSummaryRows.map((row) => (
            <div
              key={row.payment_method}
              className="rounded-xl border border-separator bg-surface-secondary/30 px-4 py-3"
            >
              <p className="text-xs font-medium uppercase tracking-wide text-label-secondary">
                {paymentMethodLabel(row.payment_method)}
              </p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-label">
                {formatCurrency(row.total_amount, currency)}
              </p>
              <p className="mt-0.5 text-xs text-label-secondary">
                {row.count} Buchung{row.count === 1 ? "" : "en"}
              </p>
            </div>
          ))}
        </div>
      ) : null}

      <form
        onSubmit={onFilterSubmit}
        className="grid gap-3 rounded-xl border border-separator bg-surface-secondary/30 p-4 sm:grid-cols-2 lg:grid-cols-5"
      >
        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Von</span>
          <input
            type="date"
            className="eh-input w-full"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Bis</span>
          <input
            type="date"
            className="eh-input w-full"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Zahlungsart</span>
          <select
            className="eh-input w-full"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value as "" | PaymentMethod)}
          >
            <option value="">Alle</option>
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABELS[m]}
              </option>
            ))}
          </select>
        </label>
        <div className="flex flex-wrap items-end gap-2 sm:col-span-2">
          <Button type="submit" disabled={loading}>
            Anzeigen
          </Button>
          <Button type="button" variant="secondary" onClick={setToday} disabled={loading}>
            Heute
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={exportCsv}
            disabled={loading || items.length === 0}
          >
            CSV
          </Button>
        </div>
      </form>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      <p className="text-sm text-label-secondary">
        {loading
          ? "Lade Zahlungen…"
          : total === 0
            ? "Keine Zahlungen im gewählten Zeitraum."
            : `${total} Zahlung${total === 1 ? "" : "en"}${total > PAGE_SIZE ? ` (Seite ${Math.floor(offset / PAGE_SIZE) + 1} von ${Math.ceil(total / PAGE_SIZE)})` : ""}`}
      </p>

      <Table wide emptyMessage="Keine Zahlungen für die gewählten Filter." isEmpty={!loading && items.length === 0}>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Methode</th>
            <th>Betrag</th>
            <th>Rechnung</th>
            <th>Gast</th>
            <th>Referenz</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id}>
              <td className="text-label-secondary" data-label="Datum">
                {formatDateTimeShort(p.paid_at)}
              </td>
              <td data-label="Methode">{paymentMethodLabel(p.payment_method)}</td>
              <td className="tabular-nums font-medium" data-label="Betrag">
                {formatCurrency(p.amount, currency)}
              </td>
              <td className="font-mono text-xs" data-label="Rechnung">
                {p.invoice_number}
                {p.booking_number ? (
                  <span className="mt-0.5 block font-sans text-xs font-normal text-label-secondary">
                    {p.booking_number}
                  </span>
                ) : null}
              </td>
              <td data-label="Gast">{p.guest_name}</td>
              <td className="text-label-secondary" data-label="Referenz">
                {p.reference ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {total > PAGE_SIZE ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={offset === 0 || loading}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >
            Zurück
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
          >
            Weiter
          </Button>
        </div>
      ) : null}
    </div>
  );
}
